import { useState, useEffect, useCallback } from 'react';
import { activitiesAPI } from '@/lib/api';
import { useNavigate } from 'react-router-dom';
import { useSettings } from '@/contexts/SettingsContext';
import { getActivityTypeConfig, getActivityIcon } from '@/lib/activity-types';
import { Activity, ChevronLeft, ChevronRight } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';

export default function ActivitiesPage() {
  const [activities, setActivities] = useState([]);
  const [total, setTotal] = useState(0);
  const [typeFilter, setTypeFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [limit] = useState(50);
  const navigate = useNavigate();
  const { settings, formatDateTime } = useSettings();
  const activityTypes = (settings.activity_types || []).map(t => typeof t === 'object' ? t : { key: t, label: t });

  const fetchActivities = useCallback(async () => {
    try {
      const params = { 
        skip: (page - 1) * limit,
        limit: limit 
      };
      if (typeFilter) params.type = typeFilter;
      const res = await activitiesAPI.list(params);
      setActivities(res.data.activities);
      setTotal(res.data.total);
    } catch (e) {  } finally { setLoading(false); }
  }, [typeFilter, page, limit]);

  useEffect(() => {
    setPage(1); // Reset to page 1 when filter changes
  }, [typeFilter]);

  useEffect(() => { fetchActivities(); }, [fetchActivities]);

  // Scroll to top when page changes
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [page]);

  const totalPages = Math.ceil(total / limit);
  const hasNextPage = page < totalPages;
  const hasPrevPage = page > 1;

  return (
    <div className="space-y-5 fade-in" data-testid="activities-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Aktivitäten</h1>
          <p className="text-muted-foreground text-sm">{total} Interaktionen gesamt</p>
        </div>
        <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value === 'all' ? '' : e.target.value)} data-testid="activity-type-filter" className="flex h-9 w-[160px] rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm">
          <option value="all">Alle Typen</option>
          {activityTypes.map(t => <option key={t.key} value={t.key}>{t.label}</option>)}
        </select>
      </div>

      <div className="space-y-2">
        {activities.map((act) => {
          const cfg = getActivityTypeConfig(act.type);
          const Icon = cfg.icon;
          return (
            <Card key={act.id} className="border-border/60 hover:bg-muted/20 transition-colors cursor-pointer" onClick={() => navigate(`/leads/${act.lead_id}`)} data-testid={`activity-${act.id}`}>
              <CardContent className="p-4 flex items-start gap-4">
                <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${cfg.bg}`}>
                  <Icon className="w-4 h-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium capitalize">{act.type}</span>
                    {act.outcome && <Badge variant="secondary" className="text-[10px]">{act.outcome.replace(/_/g, ' ')}</Badge>}
                  </div>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    <span className="font-medium text-foreground">{act.created_by_name}</span> mit <span className="font-medium text-foreground">{act.lead_name}</span>
                  </p>
                  {act.notes && <p className="text-xs text-muted-foreground mt-1 line-clamp-1">{act.notes}</p>}
                </div>
                <span className="text-[10px] mono text-muted-foreground flex-shrink-0">{formatDateTime(act.created_at)}</span>
              </CardContent>
            </Card>
          );
        })}
        {activities.length === 0 && !loading && (
          <div className="text-center py-16 text-muted-foreground">
            <Activity className="w-12 h-12 mx-auto mb-3 opacity-30" />
            <p className="text-sm">Noch keine Aktivitäten vorhanden</p>
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between pt-4 border-t">
          <p className="text-sm text-muted-foreground">
            Zeige {((page - 1) * limit) + 1}-{Math.min(page * limit, total)} von {total} Aktivitäten
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(p => p - 1)}
              disabled={!hasPrevPage}
              data-testid="prev-page-btn"
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <span className="text-sm">Seite {page} von {totalPages}</span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(p => p + 1)}
              disabled={!hasNextPage}
              data-testid="next-page-btn"
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
